import {
    createFeatureCard,
    FeatureCard,
    IFeatureCardItemProperties,
} from '@vfde-brix/ws10/feature-card';
import {
    BUTTON_COLOR_TERTIARY,
    BUTTON_SIZE_STANDARD,
    BUTTON_WIDTH_VARIATION_FULLWIDTH,
} from '@vfde-brix/ws10/button';
import {
    Price,
    removeUndefinedFieldsFromObject,
} from '@vfde-brix/ws10/core';
import { StateProps } from '../container/App/interfaces/state';
import {
    HIGHLIGHT_BADGE_SIZE_SMALL,
    HighlightBadgeColor,
    IHighlightBadgeProperties,
} from '@vfde-brix/ws10/highlight-badge';
import {
    PromoPriceAlign,
    PromoPriceVariant,
} from '@vfde-brix/ws10/promo-price';
import { getPriceForProduct } from '../container/App/helpers/priceHelper';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import {
    PriceType,
    UpsellFacetValue,
} from '@vfde-sails/glados-v2';
import {
    selectError,
    selectHasError,
    selectInsurance,
    selectRedPlusIds,
    selectSelectedProducts,
    toggleProduct,
} from '../container/App/slice';
import {
    store,
    useDispatch,
} from '../store';
import { fetchOverlay } from '@vfde-sails/overlay';
import { PRODUCT_CONTAINER } from '../container/App/constants';
import { startAppListening } from '../container/App/listener';
import {
    createStructuredAppSelector,
    selectProducts,
    selectProductsByCategory,
} from '../container/App/selectors';
import { ConnectMoreErrorType } from '../container/App/interfaces/error';

/**
 * Product card
 */
export default function mountFeatureCard (): FeatureCard | null {
    const dispatch = useDispatch();

    const container = document.getElementById(PRODUCT_CONTAINER);

    const featureCard = container && createFeatureCard(container, {
        featureCardItems: [],
        business: {
            onButtonClick: (_event: Event, id: string) => {
                const product = selectProducts(store.getState())
                    .find(productObj => productObj.id.toString() === id.toString());
                product && dispatch(toggleProduct(product));
            },
            onLinkClick: (event: Event, id: string, href: string) => {
                event.preventDefault();

                dispatch(fetchOverlay({
                    url: href,
                    context: {
                        id,
                        trackingData: {
                            trackingName: 'produktdetails',
                        },
                    },
                }));
            },
        },
    });

    const selectFeatureCardState = createStructuredAppSelector({
        products: selectProductsByCategory,
        selectedProducts: selectSelectedProducts,
        insurance: selectInsurance,
        redPlusIds: selectRedPlusIds,
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectProductsByCategory(currentState) !== selectProductsByCategory(previousState)
            || selectSelectedProducts(currentState) !== selectSelectedProducts(previousState),
        effect: (_action, api) => {
            api.cancelActiveListeners();

            const state = selectFeatureCardState(api.getState());

            featureCard?.update({
                featureCardItems: convertApiProductsToFeatureCardItems(state),
            });
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectHasError(currentState) !== selectHasError(previousState),
        effect: (_action, api) => {
            api.cancelActiveListeners();

            const error = selectError(api.getState());

            featureCard?.update({
                featureCardItems: featureCard.getProperties().featureCardItems.map(item => ({
                    ...item,
                    optDisabled: error?.type === ConnectMoreErrorType.TooManyRedPlus && item.optChecked !== true,
                })),
            });
        },
    });

    return featureCard;
}

/**
 * Convert products from API to feature card item properties
 */
export const convertApiProductsToFeatureCardItems = (
    state: Pick<StateProps, 'products' | 'redPlusIds' | 'selectedProducts'>,
): IFeatureCardItemProperties[] => {
    const { products: items, redPlusIds, selectedProducts } = state;

    return items?.map(item => {
        let highlightBadge: IHighlightBadgeProperties | undefined;
        const { overlays, highlightBadges, featureCard } = window[ADDITIONAL_PAGE_OPTIONS] || {};
        const highlightBadgeFromCms = highlightBadges?.[item.tariffId || item.id];

        if (highlightBadgeFromCms) {
            highlightBadge = {
                txtContent: highlightBadgeFromCms.text,
                optColor: highlightBadgeFromCms.color as HighlightBadgeColor,
                optSize: HIGHLIGHT_BADGE_SIZE_SMALL,
                business: {},
            };

            if (highlightBadgeFromCms.tooltip) {
                highlightBadge.containerTooltip = {
                    business: {},
                    stdTooltipId: `tooltip-${item.tariffId}`,
                    stdHeadline: highlightBadgeFromCms.tooltip.headline,
                    txtContent: highlightBadgeFromCms.tooltip.text,
                };
            }
        }

        const { promoPrice, moreCta, images } = featureCard || {};
        const { label: { [PriceType.Onetime]: oneTimePriceLabel, [PriceType.Monthly]: monthlyPriceLabel } } = promoPrice || {};

        const overlayForCategory: { [productId: string]: string } | string = overlays?.[item.category.value];

        const price = getPriceForProduct(item.price, item.category);

        return removeUndefinedFieldsFromObject<IFeatureCardItemProperties>({
            stdId: item.id,
            imgSrc: item.imageUrls[0] || images[item.tariffId || item.id],
            stdImageAlt: item.label,
            stdHeadline: item.label,
            stdLinkText: moreCta,
            highlightBadge,
            ...price && {
                promoPrice: {
                    price: Price.fromNumber(price.value || 0),
                    optSizeVariation: PromoPriceVariant.Medium,
                    stdConditionAfter: price.type === PriceType.Onetime ? oneTimePriceLabel : monthlyPriceLabel,
                    optAlign: PromoPriceAlign.Left,
                    business: {},
                },
            },
            button: {
                stdContent: '',
                optColor: BUTTON_COLOR_TERTIARY,
                optWidthVariation: BUTTON_WIDTH_VARIATION_FULLWIDTH,
                optSize: BUTTON_SIZE_STANDARD,
                business: {},
            },
            linkHref:
                item.overlayUrl
                || (typeof overlayForCategory === 'string' && overlayForCategory)
                || (overlayForCategory as Exclude<typeof overlayForCategory, string>)?.[item.tariffId || item.id]
                || undefined,
            optChecked:
                ((item.category.value === UpsellFacetValue.Redplus && redPlusIds.indexOf(item.id.toString()) !== -1)
                    || selectedProducts?.some(product => product.id === item.id)),
        });
    }) || [];
};
